/** Loading / empty / error presentational states used across pages. */
import type { ReactNode } from 'react';

export function Spinner({ label }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center gap-3 text-slate-500">
      <span
        aria-hidden
        className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600"
      />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-slate-200 bg-white">
      <Spinner label={label} />
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  icon,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
      {icon && <div className="mb-1 text-2xl">{icon}</div>}
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {hint && <p className="max-w-md text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center gap-3 rounded-xl border border-red-200 bg-red-50 p-8 text-center">
      <p className="text-sm font-medium text-red-800">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Retry
        </button>
      )}
    </div>
  );
}
